<template>
  <div class="memory-curve-page">
    <div class="page-header">
      <h1>记忆曲线</h1>
      <p>基于艾宾浩斯遗忘曲线的智能复习系统</p>
    </div>

    <div class="curve-introduction">
      <div class="intro-content">
        <div class="intro-text">
          <h2>什么是记忆曲线？</h2>
          <p>德国心理学家赫尔曼·艾宾浩斯(Hermann Ebbinghaus)研究发现，遗忘在学习之后立即开始，而且遗忘的进程并不是均匀的。最初遗忘速度很快，以后逐渐缓慢。</p>
          <p>通过科学的复习间隔，可以有效提高记忆保持率，让学习更加高效。</p>
        </div>
        
        <div class="curve-chart">
          <div class="chart-container">
            <div class="chart-title">艾宾浩斯遗忘曲线</div>
            <div class="chart-area">
              <div class="y-axis">
                <span>记忆保持率</span>
                <div class="y-labels">
                  <span>100%</span>
                  <span>80%</span>
                  <span>60%</span>
                  <span>40%</span>
                  <span>20%</span>
                  <span>0%</span>
                </div>
              </div>
              <div class="chart-content">
                <div class="curve-line forgetting-curve"></div>
                <div class="curve-line retention-curve"></div>
                <div class="repetition-points">
                  <div class="point" style="left: 20%; top: 60%;">1天</div>
                  <div class="point" style="left: 40%; top: 40%;">3天</div>
                  <div class="point" style="left: 60%; top: 30%;">7天</div>
                  <div class="point" style="left: 80%; top: 25%;">15天</div>
                </div>
              </div>
              <div class="x-axis">
                <span>时间</span>
              </div>
            </div>
            <div class="chart-legend">
              <div class="legend-item">
                <div class="legend-color forgetting"></div>
                <span>遗忘曲线</span>
              </div>
              <div class="legend-item">
                <div class="legend-color retention"></div>
                <span>复习后保持率</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="review-status">
      <div class="status-card">
        <div class="status-header">
          <h3>复习状态</h3>
          <el-button type="primary" @click="loadReviewWords" :loading="loading">
            <i class="el-icon-refresh"></i>
            刷新
          </el-button>
        </div>
        
        <div class="status-content" v-if="!loading">
          <div class="status-item">
            <div class="status-icon overdue">
              <i class="el-icon-warning"></i>
            </div>
            <div class="status-info">
              <div class="status-label">逾期复习</div>
              <div class="status-value">{{ reviewStats.overdueCount }} 个单词</div>
            </div>
          </div>
          
          <div class="status-item">
            <div class="status-icon today">
              <i class="el-icon-calendar"></i>
            </div>
            <div class="status-info">
              <div class="status-label">今日复习</div>
              <div class="status-value">{{ reviewStats.todayCount }} 个单词</div>
            </div>
          </div>
          
          <div class="status-item">
            <div class="status-icon total">
              <i class="el-icon-collection"></i>
            </div>
            <div class="status-info">
              <div class="status-label">总计待复习</div>
              <div class="status-value">{{ reviewStats.totalCount }} 个单词</div>
            </div>
          </div>
        </div>
        
        <div class="loading-placeholder" v-if="loading">
          <i class="el-icon-loading"></i>
          <span>正在加载复习数据...</span>
        </div>
      </div>
    </div>

    <div class="action-section">
      <div class="action-card">
        <div class="action-header">
          <h3>开始复习</h3>
          <p>选择复习模式，开始你的记忆曲线之旅</p>
        </div>
        
        <div class="action-buttons">
          <el-button 
            type="success" 
            size="large" 
            @click="startMemoryCurveReview"
            class="start-button"
          >
            <i class="el-icon-play"></i>
            开始记忆曲线复习
          </el-button>
          
          <el-button 
            type="info" 
            size="large" 
            @click="showSettings = true"
            class="settings-button"
          >
            <i class="el-icon-setting"></i>
            复习设置
          </el-button>
        </div>
        
        <div class="review-info" v-if="reviewWords.length > 0">
          <div class="info-item">
            <span class="info-label">预计复习时间:</span>
            <span class="info-value">{{ Math.ceil(limitedReviewWords.length * 0.5) }} 分钟</span>
          </div>
          <div class="info-item">
            <span class="info-label">本次复习单词数:</span>
            <span class="info-value">{{ limitedReviewWords.length }} 个</span>
          </div>
          <div class="info-item" v-if="reviewWords.length > reviewSettings.wordCount">
            <span class="info-label">剩余待复习:</span>
            <span class="info-value">{{ reviewWords.length - reviewSettings.wordCount }} 个</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 记忆曲线弹窗 -->
    <MemoryCurveDialog
      v-model="showMemoryCurveDialog"
      :reviewWords="limitedReviewWords"
      @review-completed="handleReviewCompleted"
    />

    <!-- 设置弹窗 -->
    <el-dialog
      title="复习设置"
      :visible.sync="showSettings"
      width="500px"
      center
    >
      <div class="settings-content">
        <div class="setting-item">
          <label>每次复习单词数:</label>
          <el-input-number
            v-model="reviewSettings.wordCount"
            :min="5"
            :max="50"
            :step="5"
          ></el-input-number>
        </div>
        
        <div class="setting-item">
          <label>复习提醒:</label>
          <el-switch
            v-model="reviewSettings.reminder"
            active-text="开启"
            inactive-text="关闭"
          ></el-switch>
        </div>
        
        <div class="setting-item">
          <label>自动播放发音:</label>
          <el-switch
            v-model="reviewSettings.autoPlay"
            active-text="开启"
            inactive-text="关闭"
          ></el-switch>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="showSettings = false">取消</el-button>
        <el-button type="primary" @click="saveSettings">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import MemoryCurveDialog from "@/components/MemoryCurveDialog.vue";
import { homeApi } from "@/api/home";
import { UserSettingsAPI } from "@/api/userSettings";

export default {
  name: "MemoryCurve",
  components: {
    MemoryCurveDialog
  },
  data() {
    return {
      loading: false,
      showMemoryCurveDialog: false,
      showSettings: false,
      reviewWords: [],
      reviewStats: {
        overdueCount: 0,
        todayCount: 0,
        totalCount: 0
      },
      reviewSettings: {
        wordCount: 5, // 临时默认值，将从数据库加载
        reminder: true,
        autoPlay: false
      }
    };
  },
  computed: {
    estimatedTime() {
      return Math.ceil(this.reviewWords.length * 0.5);
    },
    
    // 限制后的复习单词（用于弹窗）
    limitedReviewWords() {
      return this.reviewWords.slice(0, this.reviewSettings.wordCount);
    }
  },
  async mounted() {
    await this.loadUserSettings();
    await this.loadReviewWords();
  },
  methods: {
    // 加载复习单词
    async loadReviewWords() {
      this.loading = true;
      try {
        console.log("开始加载记忆曲线复习单词...");
        const response = await homeApi.getMemoryCurveReviewWords();
        console.log("记忆曲线复习单词API响应:", response);
        
        // 提取单词数据和统计数据
        this.reviewWords = response.words || [];
        this.reviewStats = response.stats || {
          overdueCount: 0,
          todayCount: 0,
          totalCount: 0
        };
        
        console.log("提取的单词数据:", this.reviewWords);
        console.log("统计数据:", this.reviewStats);
      } catch (error) {
        console.error("加载记忆曲线复习单词失败:", error);
        this.$message.error("加载记忆曲线复习单词失败");
        this.reviewWords = [];
        this.reviewStats = {
          overdueCount: 0,
          todayCount: 0,
          totalCount: 0
        };
      } finally {
        this.loading = false;
      }
    },

    // 开始记忆曲线复习
    startMemoryCurveReview() {
      if (this.reviewWords.length === 0) {
        this.$message.info("当前没有需要复习的单词，请稍后再试或调整复习设置");
        return;
      }
      
      // 在开始复习时才应用数量限制
      console.log(`开始复习，限制为 ${this.reviewSettings.wordCount} 个单词，实际使用 ${this.reviewWords.slice(0, this.reviewSettings.wordCount).length} 个`);
      
      this.showMemoryCurveDialog = true;
    },

    // 处理复习完成
    handleReviewCompleted() {
      this.showMemoryCurveDialog = false;
      this.$message.success("复习完成！");
      // 重新加载统计数据
      this.loadReviewWords();
    },

    // 加载用户设置（从数据库）
    async loadUserSettings() {
      try {
        console.log("开始加载用户设置...");
        const response = await UserSettingsAPI.getUserSettings();
        console.log("用户设置API响应:", response);
        
        if (response && response.success && response.data) {
          const settings = response.data;
          console.log("获取到的设置:", settings);
          
          // 查找max_review_words设置
          const maxReviewWordsSetting = settings.find(setting => setting.key === 'max_review_words');
          if (maxReviewWordsSetting) {
            this.reviewSettings.wordCount = maxReviewWordsSetting.value;
            console.log("从数据库加载的复习单词数:", this.reviewSettings.wordCount);
          } else {
            console.log("未找到max_review_words设置，使用默认值5");
            this.reviewSettings.wordCount = 5;
          }
          
          // 查找其他相关设置
          const autoPlaySetting = settings.find(setting => setting.key === 'auto_play_audio');
          if (autoPlaySetting) {
            this.reviewSettings.autoPlay = autoPlaySetting.value;
          }
          
          const reminderSetting = settings.find(setting => setting.key === 'cancel_auto_review');
          if (reminderSetting) {
            this.reviewSettings.reminder = !reminderSetting.value; // cancel_auto_review的反值
          }
          
          console.log("最终设置:", this.reviewSettings);
        } else {
          console.log("获取用户设置失败，使用默认值");
          this.reviewSettings.wordCount = 5;
        }
      } catch (error) {
        console.error("加载用户设置失败:", error);
        this.reviewSettings.wordCount = 5; // 失败时使用默认值
      }
    },

    // 保存设置（到数据库）
    async saveSettings() {
      try {
        console.log("保存设置到数据库:", this.reviewSettings);
        
        // 构建要保存的设置数组
        const settingsToSave = [
          {
            key: 'max_review_words',
            value: this.reviewSettings.wordCount
          },
          {
            key: 'auto_play_audio',
            value: this.reviewSettings.autoPlay
          },
          {
            key: 'cancel_auto_review',
            value: !this.reviewSettings.reminder // 取反值
          }
        ];
        
        const response = await UserSettingsAPI.updateUserSettings(settingsToSave);
        
        if (response && response.success) {
          this.showSettings = false;
          this.$message.success("设置已保存到数据库");
          // 重新加载设置以确保同步
          await this.loadUserSettings();
        } else {
          this.$message.error("保存设置失败: " + (response.message || "未知错误"));
        }
      } catch (error) {
        console.error("保存设置失败:", error);
        this.$message.error("保存设置失败: " + error.message);
      }
    }
  }
};
</script>

<style scoped>
.memory-curve-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  color: #2c3e50;
  font-size: 32px;
  margin-bottom: 10px;
}

.page-header p {
  color: #7f8c8d;
  font-size: 16px;
}

.curve-introduction {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 40px;
  color: white;
}

.intro-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
}

.intro-text h2 {
  font-size: 24px;
  margin-bottom: 20px;
}

.intro-text p {
  line-height: 1.6;
  margin-bottom: 15px;
  opacity: 0.9;
}

.curve-chart {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.chart-container {
  position: relative;
  height: 200px;
}

.chart-title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}

.chart-area {
  position: relative;
  height: 150px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 10px;
}

.y-axis {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.y-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.chart-content {
  position: absolute;
  left: 40px;
  top: 0;
  right: 0;
  bottom: 20px;
}

.curve-line {
  position: absolute;
  height: 2px;
  border-radius: 1px;
}

.forgetting-curve {
  top: 20%;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #ff6b6b, #ffa500);
  opacity: 0.8;
}

.retention-curve {
  top: 60%;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #4ecdc4, #44a08d);
  opacity: 0.8;
}

.repetition-points {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.point {
  position: absolute;
  background: #fff;
  color: #333;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  transform: translate(-50%, -50%);
}

.x-axis {
  position: absolute;
  bottom: -20px;
  left: 40px;
  right: 0;
  text-align: center;
  font-size: 12px;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.legend-color {
  width: 20px;
  height: 3px;
  border-radius: 2px;
}

.legend-color.forgetting {
  background: linear-gradient(90deg, #ff6b6b, #ffa500);
}

.legend-color.retention {
  background: linear-gradient(90deg, #4ecdc4, #44a08d);
}

.review-status {
  margin-bottom: 40px;
}

.status-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.status-header h3 {
  color: #2c3e50;
  margin: 0;
}

.status-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #409EFF;
}

.status-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.status-icon.overdue {
  background: #f56c6c;
}

.status-icon.today {
  background: #e6a23c;
}

.status-icon.total {
  background: #409EFF;
}

.status-info {
  flex: 1;
}

.status-label {
  color: #666;
  font-size: 14px;
  margin-bottom: 5px;
}

.status-value {
  color: #2c3e50;
  font-size: 18px;
  font-weight: bold;
}

.loading-placeholder {
  text-align: center;
  padding: 40px;
  color: #666;
}

.loading-placeholder i {
  font-size: 24px;
  margin-right: 10px;
}

.action-section {
  margin-bottom: 40px;
}

.action-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.action-header h3 {
  color: #2c3e50;
  font-size: 24px;
  margin-bottom: 10px;
}

.action-header p {
  color: #7f8c8d;
  margin-bottom: 30px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
}

.start-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  padding: 15px 30px;
  font-size: 16px;
}

.settings-button {
  background: #f8f9fa;
  border: 2px solid #e4e7ed;
  color: #666;
}

.review-info {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.info-item {
  text-align: center;
}

.info-label {
  display: block;
  color: #666;
  font-size: 14px;
  margin-bottom: 5px;
}

.info-value {
  display: block;
  color: #409EFF;
  font-size: 18px;
  font-weight: bold;
}

.settings-content {
  padding: 20px 0;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-item label {
  font-weight: 500;
  color: #2c3e50;
}

.dialog-footer {
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .memory-curve-page {
    padding: 10px;
  }
  
  .intro-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .curve-introduction {
    padding: 20px;
  }
  
  .status-content {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .start-button,
  .settings-button {
    width: 200px;
  }
  
  .review-info {
    flex-direction: column;
    gap: 20px;
  }
}
</style>
